/**
 * UpgradeModal — shown whenever a freemium gate (HTTP 403) is hit.
 */

import { useState } from 'react'
import { X, Zap, Check } from 'lucide-react'
import { UpgradeRequiredError } from '../lib/api'

interface Props {
  error: UpgradeRequiredError
  onClose: () => void
}

const FREE_FEATURES = [
  'Full nudge engine (55+ templates)',
  'Slack + Email delivery',
  'Engagement scoring & analytics',
  'Accountability buddy matching',
  '5 shareable reports / month',
  'Up to 50 employees',
]

const ENTERPRISE_FEATURES = [
  'Everything in Free',
  'Microsoft Teams bot',
  'SSO — Okta, Azure AD, any OIDC',
  'Unlimited shareable reports',
  'Custom nudge tracks',
  'Unlimited employees',
  'Data residency (EU or US)',
  'Advanced AI sentiment analysis',
  'SLA + dedicated support',
  'SOC 2 documentation',
]

export function UpgradeModal({ error, onClose }: Props) {
  const reason = parseReason(error.message)

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/25 backdrop-blur-sm"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div
        className="rounded-2xl w-full max-w-2xl shadow-card overflow-hidden border"
        style={{ background: '#FDFCF8', borderColor: '#e0d9cc' }}
      >
        <div
          className="px-6 py-5 border-b flex items-start justify-between gap-4"
          style={{ borderColor: '#e0d9cc', background: 'linear-gradient(135deg, #EBF6FB 0%, #FDFCF8 55%)' }}
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-brand-teal flex items-center justify-center shrink-0 shadow-glow-teal">
              <Zap size={18} className="text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-ink">Upgrade to Enterprise</h2>
              <p className="text-sm text-ink-muted mt-0.5">{reason}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-ink-soft hover:text-ink transition-colors mt-0.5"
            type="button"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-6 grid grid-cols-2 gap-4">
          <div
            className="rounded-xl p-4 border"
            style={{ background: '#f0ece4', borderColor: '#e0d9cc' }}
          >
            <div className="text-xs font-bold text-ink-soft uppercase tracking-wider mb-1">Current — Free</div>
            <div className="text-2xl font-bold text-ink mb-3">
              ₹0 <span className="text-sm font-normal text-ink-muted">forever</span>
            </div>
            <ul className="space-y-2">
              {FREE_FEATURES.map(f => (
                <li key={f} className="flex items-start gap-2 text-xs text-ink-muted">
                  <Check size={13} className="text-ink-soft mt-0.5 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </div>

          <div
            className="rounded-xl p-4 border relative overflow-hidden"
            style={{
              background: 'linear-gradient(180deg, #EBF6FB 0%, #FDFCF8 100%)',
              borderColor: '#a7d8e0',
            }}
          >
            <div className="absolute top-3 right-3 bg-brand-teal text-white text-xs font-bold px-2 py-0.5 rounded-full">
              Recommended
            </div>
            <div className="text-xs font-bold text-brand-teal uppercase tracking-wider mb-1">Enterprise</div>
            <div className="text-2xl font-bold text-ink mb-3">
              Custom <span className="text-sm font-normal text-ink-muted">per seat</span>
            </div>
            <ul className="space-y-2">
              {ENTERPRISE_FEATURES.map(f => (
                <li key={f} className="flex items-start gap-2 text-xs text-ink-muted">
                  <Check size={13} className="text-brand-teal mt-0.5 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="px-6 pb-6 flex items-center gap-3">
          <a
            href="mailto:enterprise@duva.app?subject=Enterprise upgrade enquiry"
            className="flex-1 text-center text-white font-semibold text-sm py-2.5 rounded-full transition-colors shadow-glow-teal"
            style={{ background: '#2A7A8A' }}
          >
            Contact us to upgrade →
          </a>
          <button
            onClick={onClose}
            type="button"
            className="px-5 py-2.5 text-sm text-ink-muted hover:text-ink border rounded-xl transition-colors"
            style={{ borderColor: '#e0d9cc' }}
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  )
}

export function useUpgradeGuard() {
  const [upgradeError, setUpgradeError] = useState<UpgradeRequiredError | null>(null)

  async function withUpgradeGuard<T>(fn: () => Promise<T>): Promise<T | null> {
    try {
      return await fn()
    } catch (err) {
      if (err instanceof UpgradeRequiredError) {
        setUpgradeError(err)
        return null
      }
      throw err
    }
  }

  return { upgradeError, setUpgradeError, withUpgradeGuard }
}

function parseReason(message: string): string {
  if (message.includes('50 employees')) return 'Your team has grown beyond the free plan limit of 50 employees.'
  if (message.includes('seats') && message.includes('plan is limited'))
    return "You've reached the member or invite limit for your current plan."
  if (message.includes('reports per month')) return "You've used all 5 shareable reports for this month."
  if (message.includes('Enterprise plan')) return 'This feature is available on the Enterprise plan.'
  return 'This feature requires an upgrade.'
}
